/**
 * CaregiverRepository — caregiver<->client links and audit access.
 *
 * - `inviteClient` goes through the security-definer RPC
 *   `invite_client_by_email` (emails live in auth.users and are never
 *   exposed to the browser); the function validates role, self-link and
 *   duplicate links server-side.
 * - Link state transitions (accept/revoke) are row updates guarded by RLS:
 *   only the participating caregiver or client can touch a link row.
 */
import { requireSupabase } from '@/lib/supabase/client'
import {
  auditEventFromRow,
  caregiverClientLinkFromRow,
  profileFromRow,
  type AuditEventRow,
  type CaregiverClientLinkRow,
  type ProfileRow,
} from '@/lib/supabase/mappers'
import type { Profile, Role } from '@/types'
import type { CaregiverRepository } from '../interfaces'
import { assertRowReturned, fail, notFoundError, requireUserId, validationError } from './helpers'

export const caregiverRepository: CaregiverRepository = {
  async listMyLinks(asRole: Role) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const column = asRole === 'caregiver' ? 'caregiver_id' : 'client_id'
    const { data, error } = await supabase
      .from('caregiver_client_links')
      .select('*')
      .eq(column, userId)
      .order('created_at', { ascending: false })
    if (error) fail(error)
    return ((data ?? []) as CaregiverClientLinkRow[]).map(caregiverClientLinkFromRow)
  },

  async inviteClient(clientEmail) {
    const supabase = requireSupabase()
    await requireUserId()
    const email = clientEmail.trim().toLowerCase()
    if (!email || !email.includes('@')) {
      fail(validationError('EMAIL_INVALID', 'Vul een geldig e-mailadres in.'))
    }
    const { data, error } = await supabase.rpc('invite_client_by_email', { client_email: email })
    if (error) fail(error)
    return caregiverClientLinkFromRow(assertRowReturned(data, null) as CaregiverClientLinkRow)
  },

  async acceptLink(linkId) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    // Only the client side of the link may accept (RLS restricts the row,
    // this code-level guard makes the intent explicit).
    const { data, error } = await supabase
      .from('caregiver_client_links')
      .update({ status: 'active', accepted_at: new Date().toISOString(), revoked_at: null })
      .eq('id', linkId)
      .eq('client_id', userId)
      .eq('status', 'pending')
      .select()
      .maybeSingle()
    if (error) fail(error)
    if (!data) fail(notFoundError('Deze koppeling bestaat niet of is al verwerkt.'))
    return caregiverClientLinkFromRow(data as CaregiverClientLinkRow)
  },

  async revokeLink(linkId) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    // Either participant may revoke; access stops immediately (RLS policies
    // only grant on status = 'active').
    const { data, error } = await supabase
      .from('caregiver_client_links')
      .update({ status: 'revoked', revoked_at: new Date().toISOString() })
      .eq('id', linkId)
      .or(`caregiver_id.eq.${userId},client_id.eq.${userId}`)
      .neq('status', 'revoked')
      .select()
      .maybeSingle()
    if (error) fail(error)
    if (!data) fail(notFoundError('Deze koppeling bestaat niet of is al ingetrokken.'))
    return caregiverClientLinkFromRow(data as CaregiverClientLinkRow)
  },

  async listLinkedClients(): Promise<Profile[]> {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data: links, error: linkError } = await supabase
      .from('caregiver_client_links')
      .select('client_id')
      .eq('caregiver_id', userId)
      .eq('status', 'active')
    if (linkError) fail(linkError)
    const clientIds = ((links ?? []) as { client_id: string }[]).map((link) => link.client_id)
    if (clientIds.length === 0) return []
    // RLS on profiles grants the linked caregiver read access to these rows.
    const { data, error } = await supabase.from('profiles').select('*').in('id', clientIds)
    if (error) fail(error)
    return ((data ?? []) as ProfileRow[]).map(profileFromRow)
  },

  async listAuditForClient(clientId, limit = 50) {
    const supabase = requireSupabase()
    await requireUserId()
    const { data, error } = await supabase
      .from('audit_events')
      .select('*')
      .eq('subject_user_id', clientId)
      .order('created_at', { ascending: false })
      .limit(limit)
    if (error) fail(error)
    return ((data ?? []) as AuditEventRow[]).map(auditEventFromRow)
  },
}
